import { useEffect, useRef, useState, useCallback } from 'react';
import { ArrowRight, BarChart3, Shield, Zap, Phone, Clock, DollarSign, Globe, Bot, Headphones, Building2, ChevronRight } from 'lucide-react';
import VoiceSphere from '../components/VoiceSphere';
import LiveDemoWidget from '../components/LiveDemoWidget';

const PORTAL_URL = import.meta.env.VITE_PORTAL_URL || 'http://localhost:3200';
/* ── Animated Counter ── */
function AnimatedCounter({ target, suffix = '', duration = 1500 }: {
    target: number;
    suffix?: string;
    duration?: number;
}) {
    const ref = useRef<HTMLSpanElement>(null);
    const counted = useRef(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting && !counted.current) {
                counted.current = true;
                const start = performance.now();
                const animate = (now: number) => {
                    const p = Math.min((now - start) / duration, 1);
                    const eased = 1 - Math.pow(1 - p, 4);
                    el.textContent = `${Math.round(target * eased).toLocaleString()}${suffix}`;
                    if (p < 1) requestAnimationFrame(animate);
                };
                requestAnimationFrame(animate);
            }
        }, { threshold: 0.3 });

        observer.observe(el);
        return () => observer.disconnect();
    }, [target, suffix, duration]);

    return (
        <span
            ref={ref}
            className="font-display font-bold"
            style={{ fontSize: '2rem', color: 'var(--color-starlight)' }}
        >
            0{suffix}
        </span>
    );
}

/* ── Data ── */
const STATS = [
    { value: 98, suffix: '%', label: 'Booking Success Rate' },
    { value: 340, suffix: 'ms', label: 'Avg Response Latency' },
    { value: 60, suffix: '%', label: 'Cost Reduction' },
    { value: 24, suffix: '/7', label: 'Always Available' },
];

const FEATURES = [
    { icon: Bot, title: 'Autonomous Agents', description: 'AI agents that handle entire call workflows — booking, rescheduling, confirmations — without human intervention.', color: '#8b5cf6' },
    { icon: Phone, title: 'Multi-Provider Telephony', description: 'Plug in Retell, Twilio, or Vonage. Switch providers without changing a line of code.', color: '#2dd4bf' },
    { icon: Shield, title: 'CASL & TCPA Compliant', description: 'Built-in consent tracking, DNC list integration, and audit trails that keep you compliant.', color: '#34d399' },
    { icon: BarChart3, title: 'Real-time Analytics', description: 'Track call volume, conversion rates, cost per call, and agent performance — all in real-time.', color: '#f59e0b' },
    { icon: Clock, title: 'Sub-Second Latency', description: 'Enterprise-grade voice pipeline with <500ms end-to-end latency for natural conversations.', color: '#a78bfa' },
    { icon: DollarSign, title: 'Transparent Pricing', description: 'Per-minute billing with no hidden fees. Track costs in real-time and set budget alerts.', color: '#8a94b2' },
];

const INDUSTRIES = [
    { icon: Building2, name: 'Healthcare', desc: 'Patient intake, appointment scheduling, prescription reminders' },
    { icon: Headphones, name: 'Restaurants', desc: 'Reservation management, order taking, customer callbacks' },
    { icon: Globe, name: 'Mortgage', desc: 'Lead qualification, rate quotes, application follow-ups' },
    { icon: Zap, name: 'Athlete Mgmt', desc: 'Training reminders, booking coordination, fan engagement' },
];

export default function Home() {
    const [showDemo, setShowDemo] = useState(false);
    const demoRef = useRef<HTMLDivElement>(null);

    const handleTryDemo = useCallback(() => {
        setShowDemo(true);
        // Scroll the demo into view smoothly after it becomes visible
        setTimeout(() => {
            demoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
    }, []);

    return (
        <div>
            {/* ═══════════════════ HERO ═══════════════════ */}
            <section
                style={{
                    position: 'relative',
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    overflow: 'hidden',
                    paddingTop: '5rem',
                    paddingBottom: '4rem',
                }}
            >
                {/* Aurora backgrounds */}
                <div className="aurora aurora-1" />
                <div className="aurora aurora-2" />
                <div className="aurora aurora-3" />

                <div className="section-container" style={{ position: 'relative', zIndex: 10, width: '100%' }}>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr',
                            gap: '3rem',
                            alignItems: 'center',
                        }}
                        className="hero-grid"
                    >
                        {/* LEFT — Copy */}
                        <div style={{ maxWidth: '620px' }}>
                            {/* Badge */}
                            <div
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: '0.375rem 1rem',
                                    borderRadius: '999px',
                                    background: 'rgba(13, 16, 37, 0.7)',
                                    border: '1px solid rgba(255,255,255,0.08)',
                                    fontSize: '0.8rem',
                                    color: 'var(--color-stardust)',
                                    marginBottom: '1.5rem',
                                    backdropFilter: 'blur(12px)',
                                }}
                            >
                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#34d399', display: 'inline-block' }} className="animate-pulse" />
                                Now in production — healthcare, restaurant & more
                            </div>

                            {/* Headline */}
                            <h1
                                className="font-display"
                                style={{
                                    fontSize: 'clamp(2.5rem, 5vw, 3.75rem)',
                                    fontWeight: 800,
                                    lineHeight: 1.08,
                                    letterSpacing: '-0.025em',
                                    color: 'var(--color-starlight)',
                                    marginBottom: '1.5rem',
                                }}
                            >
                                Voice agents that{' '}
                                <span className="gradient-text">call, book & convert</span>
                                {' '}— autonomously
                            </h1>

                            {/* Subtext */}
                            <p style={{
                                fontSize: '1.125rem',
                                lineHeight: 1.7,
                                color: 'var(--color-stardust)',
                                opacity: 0.8,
                                maxWidth: '500px',
                                marginBottom: '2rem',
                            }}>
                                Deploy AI voice agents in minutes. They handle inbound and outbound calls,
                                book appointments, qualify leads, and follow up — so your team can focus on what matters.
                            </p>

                            {/* CTAs */}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.875rem', marginBottom: '2rem' }}>
                                <a href={`${PORTAL_URL}/signup`} className="btn-primary" style={{ padding: '0.875rem 1.75rem', fontSize: '1rem' }}>
                                    Start Free Trial
                                    <ArrowRight style={{ width: 18, height: 18 }} />
                                </a>
                                <button
                                    onClick={handleTryDemo}
                                    className="btn-secondary"
                                    style={{ padding: '0.875rem 1.75rem', fontSize: '1rem' }}
                                >
                                    <Phone style={{ width: 18, height: 18 }} />
                                    Try Live Demo
                                </button>
                            </div>

                            {/* Trust badges */}
                            <div style={{ display: 'flex', gap: '1.5rem', opacity: 0.5, flexWrap: 'wrap' }}>
                                {[
                                    { icon: Shield, text: 'CASL Compliant' },
                                    { icon: Clock, text: '<500ms Latency' },
                                    { icon: Zap, text: '99.9% Uptime' },
                                ].map((badge, i) => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.75rem', color: 'var(--color-stardust)' }}>
                                        <badge.icon style={{ width: 14, height: 14 }} />
                                        {badge.text}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* RIGHT — Live Demo Widget (always visible on desktop) */}
                        <div
                            ref={demoRef}
                            className="hero-demo-col"
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                            }}
                        >
                            <LiveDemoWidget />
                        </div>
                    </div>
                </div>
            </section>

            {/* ═══════════════════ STATS ═══════════════════ */}
            <section style={{ position: 'relative', zIndex: 10, marginTop: '-2rem' }}>
                <div className="section-container" style={{ maxWidth: '960px' }}>
                    <div
                        className="glass"
                        style={{
                            borderRadius: '16px',
                            padding: '2rem',
                            display: 'grid',
                            gridTemplateColumns: 'repeat(4, 1fr)',
                            gap: '1rem',
                        }}
                    >
                        {STATS.map((s, i) => (
                            <div key={i} style={{ textAlign: 'center' }}>
                                <AnimatedCounter target={s.value} suffix={s.suffix} />
                                <p style={{ fontSize: '0.7rem', color: 'var(--color-stardust)', opacity: 0.6, marginTop: '0.25rem', fontWeight: 500 }}>
                                    {s.label}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ═══════════════════ FEATURES ═══════════════════ */}
            <section style={{ padding: '7rem 0 5rem' }}>
                <div className="section-container">
                    <div style={{ textAlign: 'center', maxWidth: '640px', margin: '0 auto 3rem' }}>
                        <h2
                            className="font-display"
                            style={{
                                fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
                                fontWeight: 700,
                                color: 'var(--color-starlight)',
                                marginBottom: '1rem',
                            }}
                        >
                            Everything you need to deploy{' '}
                            <span className="gradient-text">voice intelligence</span>
                        </h2>
                        <p style={{ color: 'var(--color-stardust)', opacity: 0.7, fontSize: '1.0625rem' }}>
                            From real-time call handling to deep analytics, Lumina provides
                            the complete platform to automate your voice operations.
                        </p>
                    </div>

                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                            gap: '1.25rem',
                            maxWidth: '1100px',
                            margin: '0 auto',
                        }}
                    >
                        {FEATURES.map((feat, i) => (
                            <div
                                key={i}
                                className="glass glass-hover animate-fade-in-up"
                                style={{
                                    borderRadius: '16px',
                                    padding: '1.75rem',
                                    animationDelay: `${i * 80}ms`,
                                }}
                            >
                                <div
                                    style={{
                                        width: 42,
                                        height: 42,
                                        borderRadius: 12,
                                        background: `${feat.color}20`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginBottom: '1rem',
                                    }}
                                >
                                    <feat.icon style={{ width: 20, height: 20, color: feat.color }} />
                                </div>
                                <h3
                                    className="font-display"
                                    style={{ fontWeight: 600, color: 'var(--color-starlight)', fontSize: '1.125rem', marginBottom: '0.5rem' }}
                                >
                                    {feat.title}
                                </h3>
                                <p style={{ fontSize: '0.875rem', lineHeight: 1.6, color: 'var(--color-stardust)', opacity: 0.65 }}>
                                    {feat.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ═══════════════════ HOW IT WORKS ═══════════════════ */}
            <section className="mesh-gradient" style={{ padding: '5rem 0' }}>
                <div className="section-container">
                    <div style={{ textAlign: 'center', maxWidth: '640px', margin: '0 auto 3rem' }}>
                        <h2
                            className="font-display"
                            style={{
                                fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
                                fontWeight: 700,
                                color: 'var(--color-starlight)',
                                marginBottom: '1rem',
                            }}
                        >
                            Deploy in <span className="gradient-text">three steps</span>
                        </h2>
                        <p style={{ color: 'var(--color-stardust)', opacity: 0.7 }}>
                            Go from sign-up to live voice agents in under 10 minutes.
                        </p>
                    </div>

                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                            gap: '1.5rem',
                            maxWidth: '1000px',
                            margin: '0 auto',
                        }}
                    >
                        {[
                            { step: '01', title: 'Create Your Agent', desc: 'Choose a voice, set the personality, define the call flow and tools. Our wizard makes it simple.' },
                            { step: '02', title: 'Connect Your Stack', desc: 'Plug in your calendar, CRM, or booking system. We integrate with 50+ tools out of the box.' },
                            { step: '03', title: 'Go Live', desc: 'Assign a phone number and start taking calls. Monitor everything in real-time from your dashboard.' },
                        ].map((item, i) => (
                            <div key={i} style={{ position: 'relative' }} className="animate-fade-in-up">
                                <div className="glass" style={{ borderRadius: '16px', padding: '2rem', height: '100%' }}>
                                    <span
                                        className="font-mono gradient-text"
                                        style={{ fontSize: '2.5rem', fontWeight: 700, opacity: 0.3, display: 'block', marginBottom: '1rem' }}
                                    >
                                        {item.step}
                                    </span>
                                    <h3 className="font-display" style={{ fontWeight: 600, color: 'var(--color-starlight)', fontSize: '1.25rem', marginBottom: '0.75rem' }}>
                                        {item.title}
                                    </h3>
                                    <p style={{ fontSize: '0.875rem', lineHeight: 1.65, color: 'var(--color-stardust)', opacity: 0.6 }}>
                                        {item.desc}
                                    </p>
                                </div>
                                {i < 2 && (
                                    <div
                                        className="hidden md:flex"
                                        style={{
                                            position: 'absolute',
                                            top: '50%',
                                            right: '-12px',
                                            transform: 'translateY(-50%)',
                                            zIndex: 10,
                                            color: 'var(--color-nova)',
                                            opacity: 0.3,
                                        }}
                                    >
                                        <ChevronRight style={{ width: 24, height: 24 }} />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ═══════════════════ INDUSTRIES ═══════════════════ */}
            <section style={{ padding: '5rem 0' }}>
                <div className="section-container">
                    <div style={{ textAlign: 'center', maxWidth: '640px', margin: '0 auto 3rem' }}>
                        <h2
                            className="font-display"
                            style={{
                                fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
                                fontWeight: 700,
                                color: 'var(--color-starlight)',
                                marginBottom: '1rem',
                            }}
                        >
                            Built for <span className="gradient-text">every industry</span>
                        </h2>
                        <p style={{ color: 'var(--color-stardust)', opacity: 0.7 }}>
                            Pre-built workflows and compliance rules for the industries that need voice AI most.
                        </p>
                    </div>

                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                            gap: '1.25rem',
                            maxWidth: '1000px',
                            margin: '0 auto',
                        }}
                    >
                        {INDUSTRIES.map((ind, i) => (
                            <a
                                key={i}
                                href="/industries"
                                className="glass glass-hover animate-fade-in-up"
                                style={{
                                    display: 'block',
                                    borderRadius: '16px',
                                    padding: '1.5rem',
                                    textDecoration: 'none',
                                    animationDelay: `${i * 80}ms`,
                                }}
                            >
                                <div
                                    style={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: 12,
                                        background: 'rgba(139, 92, 246, 0.12)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginBottom: '1rem',
                                    }}
                                >
                                    <ind.icon style={{ width: 20, height: 20, color: 'var(--color-nova)' }} />
                                </div>
                                <h3 className="font-display" style={{ fontWeight: 600, color: 'var(--color-starlight)', marginBottom: '0.375rem' }}>
                                    {ind.name}
                                </h3>
                                <p style={{ fontSize: '0.8rem', lineHeight: 1.55, color: 'var(--color-stardust)', opacity: 0.55 }}>
                                    {ind.desc}
                                </p>
                            </a>
                        ))}
                    </div>
                </div>
            </section>

            {/* ═══════════════════ CTA ═══════════════════ */}
            <section style={{ position: 'relative', overflow: 'hidden', padding: '5rem 0 6rem' }}>
                <div className="aurora aurora-1" style={{ opacity: 0.12 }} />
                <div className="aurora aurora-3" style={{ opacity: 0.08 }} />

                <div className="section-container" style={{ position: 'relative', zIndex: 10, textAlign: 'center', maxWidth: '800px' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2rem' }}>
                        <VoiceSphere size={120} />
                    </div>
                    <h2
                        className="font-display"
                        style={{
                            fontSize: 'clamp(1.75rem, 4vw, 3rem)',
                            fontWeight: 700,
                            color: 'var(--color-starlight)',
                            marginBottom: '1.25rem',
                            lineHeight: 1.15,
                        }}
                    >
                        Ready to put your calls on{' '}
                        <span className="gradient-text">autopilot</span>?
                    </h2>
                    <p style={{
                        fontSize: '1.125rem',
                        color: 'var(--color-stardust)',
                        opacity: 0.7,
                        maxWidth: '560px',
                        margin: '0 auto 2rem',
                    }}>
                        Join the businesses using Lumina to handle thousands of calls
                        per day — autonomously, compliantly, and at a fraction of the cost.
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '0.875rem' }}>
                        <a href={`${PORTAL_URL}/signup`} className="btn-primary glow-cta" style={{ padding: '0.875rem 2rem', fontSize: '1rem' }}>
                            Start Free — No Credit Card
                            <ArrowRight style={{ width: 18, height: 18 }} />
                        </a>
                        <button
                            onClick={handleTryDemo}
                            className="btn-secondary"
                            style={{ padding: '0.875rem 2rem', fontSize: '1rem' }}
                        >
                            Talk to Luna First
                        </button>
                    </div>
                </div>
            </section>

            {/* ═══════════════════ LIVE DEMO MODAL (mobile fallback) ═══════════════════ */}
            {showDemo && (
                <div
                    className="lg:hidden"
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 100,
                        background: 'rgba(8, 9, 26, 0.85)',
                        backdropFilter: 'blur(12px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '1rem',
                    }}
                    onClick={(e) => { if (e.target === e.currentTarget) setShowDemo(false); }}
                >
                    <div style={{ maxWidth: '380px', width: '100%' }}>
                        <LiveDemoWidget />
                        <button
                            onClick={() => setShowDemo(false)}
                            style={{
                                marginTop: '1rem',
                                width: '100%',
                                padding: '0.75rem',
                                borderRadius: '12px',
                                border: '1px solid rgba(255,255,255,0.1)',
                                background: 'transparent',
                                color: 'var(--color-stardust)',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                            }}
                        >
                            Close Demo
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
